import React, { useState, useEffect, useCallback, useId } from 'react';
import { TableSkeleton } from '../Skeleton';
import { User, UserRole, GlobalRole } from '../../types';
import { adminService } from '../../services/adminService';
import { RefreshCw, UserPlus, Trash2, ShieldCheck, Eye, Crown } from 'lucide-react';
import { ConfirmDialog } from '../ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { ADMIN_TAB_FILL, ADMIN_LIST_FILL } from './adminLayout';

interface RoleManagerProps {
  currentUser: User;
}

const ROLE_LABELS: Record<string, string> = {
  admin: '관리자',
  auditor: '감사자',
  super_admin: 'Super Admin',
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-blue-50 text-blue-700',
  auditor: 'bg-amber-50 text-amber-700',
  super_admin: 'bg-purple-50 text-purple-700',
};

const ROLE_ICONS: Record<string, React.ReactNode> = {
  admin: <ShieldCheck size={16} className="text-blue-500" />,
  auditor: <Eye size={16} className="text-amber-500" />,
  super_admin: <Crown size={16} className="text-purple-500" />,
};

export const RoleManager: React.FC<RoleManagerProps> = ({ currentUser }) => {
  const { showToast } = useToast();
  const fid = useId();
  const [revokeTarget, setRevokeTarget] = useState<{ userId: string; userName: string } | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newRole, setNewRole] = useState<GlobalRole>('auditor');
  const [granting, setGranting] = useState(false);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getRoles();
      setRoles(data);
    } catch (err) {
      console.error('Failed to load roles:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserId.trim() || !newRole) return;

    setGranting(true);
    try {
      await adminService.grantRole(newUserId.trim(), newRole, newUserName.trim() || undefined);
      setNewUserId('');
      setNewUserName('');
      setNewRole('auditor');
      setShowGrantForm(false);
      await loadRoles();
    } catch (err: any) {
      showToast({ type: 'error', message: err.message || '역할을 부여하지 못했습니다.' });
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = (userId: string, userName: string) => {
    if (String(currentUser.id) === userId) {
      showToast({ type: 'info', message: '자신의 역할은 회수할 수 없습니다.' });
      return;
    }
    setRevokeTarget({ userId, userName });
  };

  const performRevoke = async () => {
    if (!revokeTarget) return;
    const { userId } = revokeTarget;
    setRevokeTarget(null);
    try {
      await adminService.revokeRole(userId);
      setRoles(prev => prev.filter(r => r.userId !== userId));
      showToast({ type: 'success', message: '역할을 회수했습니다.' });
    } catch (err: any) {
      showToast({ type: 'error', message: err.message || '역할을 회수하지 못했습니다.' });
    }
  };

  return (
    <div className={ADMIN_TAB_FILL}>
      {/* Actions bar */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-slate-500">
          등록된 역할 {roles.length}건
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={loadRoles}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            새로고침
          </button>
          <button
            onClick={() => setShowGrantForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <UserPlus size={14} />
            역할 부여
          </button>
        </div>
      </div>

      {/* Grant form */}
      {showGrantForm && (
        <form
          onSubmit={handleGrant}
          className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-3">역할 부여</h3>
          <div className="space-y-3">
            <div>
              <label htmlFor={`${fid}-sabun`} className="block text-xs text-slate-500 mb-1">사번 (User ID) *</label>
              <input id={`${fid}-sabun`}
                type="text"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value)}
                placeholder="사번을 입력하세요"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                autoFocus
              />
            </div>
            <div>
              <label htmlFor={`${fid}-name`} className="block text-xs text-slate-500 mb-1">이름 (선택)</label>
              <input id={`${fid}-name`}
                type="text"
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
                placeholder="사용자 이름"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor={`${fid}-role`} className="block text-xs text-slate-500 mb-1">역할 *</label>
              <select id={`${fid}-role`}
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as GlobalRole)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              >
                <option value="admin">관리자 (Admin)</option>
                <option value="auditor">감사자 (Auditor)</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowGrantForm(false);
                  setNewUserId('');
                  setNewUserName('');
                }}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={granting || !newUserId.trim()}
                className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                {granting ? '부여 중...' : '부여'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Role list */}
      {loading && roles.length === 0 ? (
        <TableSkeleton rows={5} cols={3} />
      ) : roles.length === 0 ? (
        <div className="text-center py-12 text-slate-400">등록된 역할이 없습니다.</div>
      ) : (
        <div className={`space-y-2 ${ADMIN_LIST_FILL}`}>
          {roles.map(role => {
            const isMe = String(currentUser.id) === role.userId;
            return (
              <div
                key={role.userId}
                className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 bg-slate-50 rounded-lg flex items-center justify-center shrink-0">
                    {ROLE_ICONS[role.role] || <ShieldCheck size={16} className="text-slate-400" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800 truncate">
                        {role.userName || '(이름 없음)'}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                        ROLE_COLORS[role.role] || 'bg-slate-100 text-slate-600'
                      }`}>
                        {ROLE_LABELS[role.role] || role.role}
                      </span>
                      {isMe && (
                        <span className="text-xs text-brand-600">(나)</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      사번: {role.userId}
                      {role.grantedBy && (
                        <>
                          <span className="mx-1">·</span>
                          부여자: {role.grantedBy}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleRevoke(role.userId, role.userName)}
                  disabled={isMe}
                  className={`p-2 rounded-lg transition-colors shrink-0 ${
                    isMe
                      ? 'text-slate-300 cursor-not-allowed'
                      : 'text-slate-400 hover:text-brand-600 hover:bg-brand-50'
                  }`}
                  title={isMe ? '자신은 회수할 수 없습니다' : '역할 회수'}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!revokeTarget}
        title="역할 회수"
        message={`'${revokeTarget?.userName || revokeTarget?.userId || ''}' 님의 역할을 회수할까요? 즉시 일반 사용자 권한으로 돌아갑니다.`}
        confirmLabel="회수"
        danger
        onConfirm={performRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
};
